import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cta, CTA_STYLES, CTA_COLORS } from "@/lib/cta";
import { BRAND } from "@/lib/brand";
import heroBackground from "@/assets/hero-background.jpg";
import { Scale, Rocket, Shield, Target, FileCheck, ClipboardList } from "lucide-react";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { ScrollToTop } from "@/components/ui/scroll-to-top";

/**
 * Lenders Landing Page (FR/EN) – Standalone
 * - Header/Footer autonomous
 * - EN default language (toggle FR)
 * - Uses BRAND palette from e-learning
 * - CTAs: /auth?role=lender and /questionnaire (adjust as needed)
 */

const THEME = BRAND;



const copy = {
  en: {
    hero: {
      eyebrow: "LENDERS",
      title: "Access a flow of qualified financing files",
      subtitle:
        "Know the fundamental KPIs? Then you can recognize a good business. We prepare, filter and structure files — You just submit your offer.",
      ctaPrimary: "Apply to the lenders program",
      ctaSecondary: "Contact the team",
    },
    vision: {
      title: "Redefining the Future of Acquisition Financing",
      subtitle: "Innovation at the service of business transfer. At AcquizGroup, we transform the business transfer landscape by eliminating traditional barriers.",
      missionTitle: "Our mission is clear",
      missionText: "Accelerate access to capital, neutralize attribution bias, and optimize closing timelines. We don't just change processes—we reverse the odds of failure to maximize success for high-potential deals.",
    },
    philosophy: {
      title: "A more inclusive way to fund acquisitions",
      bullets: [
        "Our goal is to broaden access to acquisition financing and reduce bias in attribution.",
        "Sound businesses can be evaluated on fundamentals — e.g., ratios, profit margin and **DSCR**.",
        "AcquizGroup is a lead service: we bring you qualified borrowers without prospecting and we do not interfere with your credit decisions.",
      ],
      fee: "Registration is free. We charge **2% of lender gains** as a lead‑service fee.",
    },
    how: {
      title: "How it works",
      steps: [
        "Create your profile and select loan products",
        "Background check",
        "Qualification",
        "Receive matched company files",
        "Submit an offer (loan form with your terms & conditions)",
        "Offer displayed anonymously on the deal page",
        "After LOI signed by parties, the buyer books a meeting with you",
        "Closing",
      ],
    },
    who: {
      title: "Who can enroll as a lender?",
      bullets: [
        "Conventional and non‑conventional acquisition lenders (bridge, VC, family offices, equity, asset‑based, etc.)",
        "Commercial real‑estate lenders",
      ],
    },
    program: {
      title: "Transparency & speed",
      cards: [
        { h: "Targeted flow", p: "Files aligned with your criteria (amounts, sectors, structures)." },
        { h: "Ready‑to‑review", p: "Financials, FCF assumptions, and lender‑friendly packaging." },
        { h: "Clear process", p: "NDA → LOI → Financing → Closing with audit trail." },
      ],
    },
  },
  fr: {
    hero: {
      eyebrow: "PRÊTEURS",
      title: "Accédez à un flux de dossiers de financement qualifiés",
      subtitle:
        "Vous connaissez les KPIs fondamentaux ? Alors vous savez reconnaître une bonne entreprise. Nous préparons, filtrons et structurons les dossiers — il ne vous reste qu'à formuler votre offre.",
      ctaPrimary: "Postuler au programme de prêteurs",
      ctaSecondary: "Contacter l'équipe",
    },
    vision: {
      title: "Redéfinir l'Avenir du Financement d'Acquisition",
      subtitle: "L'innovation au service de la transmission d'entreprise. Chez AcquizGroup, nous transformons le paysage du transfert d'entreprise en éliminant les barrières traditionnelles.",
      missionTitle: "Notre mission est claire",
      missionText: "Accélérer l'accès au capital, neutraliser les biais d'attribution et optimiser les délais de clôture. Nous ne nous contentons pas de changer les processus, nous inversons les probabilités d'échec pour maximiser le succès des deals à fort potentiel.",
    },
    philosophy: {
      title: "Une approche plus inclusive du financement d'acquisition",
      bullets: [
        "Notre objectif : élargir l'accès au financement d'acquisition et réduire les biais d'attribution.",
        "La viabilité d'une entreprise se mesure sur des fondamentaux — ex. marge bénéficiaire et **DSCR > 1,3**.",
        "AcquizGroup est un service d'apport d'affaires : nous vous amenons des clients qualifiés sans prospection et nous n'interférons pas dans vos décisions de crédit.",
      ],
      fee: "L'inscription des prêteurs est **gratuite**. Nous prélevons **2 %** de vos gains en tant que frais de lead service.",
    },
    how: {
      title: "Comment ça fonctionne",
      steps: [
        "Créer votre profil et sélectionner vos types de prêts",
        "Background check",
        "Qualification",
        "Réception des dossiers correspondant à vos critères",
        "Soumettre votre offre (formulaire de prêt avec vos T&C)",
        "Offre affichée de façon anonyme sur la fiche de l'entreprise",
        "Après signature du LOI par les parties, l'acheteur planifie un rendez‑vous avec vous",
        "Closing",
      ],
    },
    who: {
      title: "Qui peut s'inscrire comme prêteur ?",
      bullets: [
        "Prêteurs en acquisition d'entreprises conventionnels et non conventionnels (bridge, VC, family offices, equity, prêts adossés à des actifs, etc.)",
        "Prêteurs en immobilier commercial",
      ],
    },
    program: {
      title: "Transparence & rapidité",
      cards: [
        { h: "Flux ciblé", p: "Dossiers alignés sur vos critères (montants, secteurs, structures)." },
        { h: "Dossiers prêts", p: "États financiers, hypothèses FCF et packaging favorable aux prêteurs." },
        { h: "Processus clair", p: "Suivi NDA → LOI → Financement → Closing avec traçabilité." },
      ],
    },
  },
} as const;

export default function LendersLandingStandalone() {
  const [lang, setLang] = useState<'en'|'fr'>('en');
  const t = useMemo(() => copy[lang], [lang]);
  const ctaTexts = cta('lender', lang);

  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader lang={lang} onToggleLang={() => setLang(lang==='en'?'fr':'en')} />

      {/* Hero */}
      <section className="w-full relative overflow-hidden" style={{ background: `linear-gradient(180deg, ${THEME.navy}, ${THEME.navyDark})` }}>
        <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
          <img src={heroBackground} alt="" className="min-w-full min-h-full object-cover blur-[2px] opacity-45" />
        </div>
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${THEME.navy}CC, ${THEME.navyDark}CC)` }}></div>
        <div className="relative mx-auto max-w-7xl px-4 py-10">
          <p className="text-sm uppercase" style={{ color: THEME.silver }}>{t.hero.eyebrow}</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-extrabold text-white">{t.hero.title}</h1>
          <p className="mt-3 max-w-4xl text-base md:text-lg text-white/85">{t.hero.subtitle}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/register/lender" className={CTA_STYLES.primary} style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}>{ctaTexts.primary}</Link>
            <Link to="/contact" aria-label={ctaTexts.aria} className={`${CTA_STYLES.secondary} border-white/30 text-white`}>{ctaTexts.secondary}</Link>
          </div>
        </div>
      </section>

      {/* Vision Section - NEW */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="text-center max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold" style={{ color: THEME.navy }}>{t.vision.title}</h2>
          <p className="mt-4 text-lg" style={{ color: THEME.graphite }}>{t.vision.subtitle}</p>
        </div>
        <div className="mt-12 max-w-4xl mx-auto">
          <h3 className="text-2xl font-semibold text-center" style={{ color: THEME.navy }}>{t.vision.missionTitle}</h3>
          <p className="mt-4 text-base text-center leading-relaxed" style={{ color: THEME.graphite }}>{t.vision.missionText}</p>
        </div>
      </section>

      {/* Strategic Choice Section */}
      <section className="mx-auto max-w-7xl px-4 pb-16">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-8" style={{ color: THEME.navy }}>
          {lang === 'fr' ? 'Pourquoi AcquizGroup est votre choix stratégique' : 'Why AcquizGroup is your strategic choice'}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: `linear-gradient(135deg, ${THEME.goldStart}30, ${THEME.goldEnd}40)` }}>
              <Scale className="w-6 h-6" style={{ color: THEME.goldEnd }} />
            </div>
            <h3 className="font-semibold text-lg mb-3" style={{ color: THEME.navy }}>
              {lang === 'fr' ? "L'Objectivité comme Standard" : 'Objectivity as Standard'}
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: THEME.graphite }}>
              {lang === 'fr' 
                ? "Nous croyons que la viabilité d'un projet repose sur la solidité de ses fondamentaux. Nous mettons en avant des indicateurs clés — tels que la marge bénéficiaire, le flux de trésorerie et le DSCR — pour garantir la qualité de chaque dossier."
                : "We believe project viability rests on solid fundamentals. We highlight key indicators — such as profit margin, cash flow, and DSCR — to ensure the quality of each file."}
            </p>
          </div>
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: `linear-gradient(135deg, ${THEME.goldStart}30, ${THEME.goldEnd}40)` }}>
              <Rocket className="w-6 h-6" style={{ color: THEME.goldEnd }} />
            </div>
            <h3 className="font-semibold text-lg mb-3" style={{ color: THEME.navy }}>
              {lang === 'fr' ? 'Performance sans Prospection' : 'Performance without Prospecting'}
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: THEME.graphite }}>
              {lang === 'fr' 
                ? "Plus qu'une simple mise en relation, nous sommes votre moteur d'opportunités. Nous vous apportons des flux de clients hautement qualifiés directement dans votre pipeline, sans aucun effort de prospection de votre part."
                : "More than just matchmaking, we are your opportunity engine. We bring highly qualified client flows directly into your pipeline, with no prospecting effort on your part."}
            </p>
          </div>
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: `linear-gradient(135deg, ${THEME.goldStart}30, ${THEME.goldEnd}40)` }}>
              <Shield className="w-6 h-6" style={{ color: THEME.goldEnd }} />
            </div>
            <h3 className="font-semibold text-lg mb-3" style={{ color: THEME.navy }}>
              {lang === 'fr' ? 'Liberté et Autonomie' : 'Freedom and Autonomy'}
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: THEME.graphite }}>
              {lang === 'fr' 
                ? "Notre rôle est de fluidifier l'écosystème. Nous qualifions les opportunités, mais vous gardez le contrôle total : nous n'interférons jamais dans vos décisions finales de crédit."
                : "Our role is to streamline the ecosystem. We qualify opportunities, but you keep total control: we never interfere with your final credit decisions."}
            </p>
          </div>
        </div>
      </section>

      {/* Program value */}
      <section className="mx-auto max-w-7xl px-4 pb-12">
        <h2 className="text-2xl md:text-3xl font-semibold" style={{ color: THEME.navy }}>{t.program.title}</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          {t.program.cards.map((c, i) => {
            const icons = [Target, FileCheck, ClipboardList];
            const Icon = icons[i];
            return (
              <div key={i} className="rounded-2xl border bg-white p-6 shadow-sm">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: `linear-gradient(135deg, ${THEME.goldStart}30, ${THEME.goldEnd}40)` }}>
                  <Icon className="w-6 h-6" style={{ color: THEME.goldEnd }} />
                </div>
                <h3 className="font-semibold" style={{ color: THEME.navy }}>{c.h}</h3>
                <p className="mt-1 text-sm" style={{ color: THEME.graphite }}>{c.p}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works - MOVED TO END - Arrow Style */}
      <section className="mx-auto max-w-7xl px-4 pb-12">
        <h2 className="text-2xl md:text-3xl font-semibold mb-8" style={{ color: THEME.navy }}>{t.how.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {t.how.steps.map((s, i) => (
            <div 
              key={i} 
              className="relative flex items-center justify-center text-center p-5 min-h-[80px]"
              style={{
                background: `linear-gradient(135deg, ${THEME.goldStart}25, ${THEME.goldEnd}35)`,
                clipPath: 'polygon(0 0, 90% 0, 100% 50%, 90% 100%, 0 100%, 10% 50%)',
                marginLeft: i === 0 ? '0' : '-8px',
              }}
            >
              <span 
                className="text-xs font-medium leading-tight px-3"
                style={{ color: THEME.navy }}
              >
                {s}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24">
        <div className="rounded-2xl p-8 text-center shadow-lg" style={{ background: `linear-gradient(180deg, ${THEME.navyDark}, ${THEME.navy})` }}>
          <h2 className="text-2xl font-semibold text-white">{lang==='fr'?"Prêt à financer l'avenir ?":"Ready to finance the future?"}</h2>
          <p className="mt-2 text-sm" style={{ color: THEME.silver }}>{lang==='fr'?"Rejoignez notre réseau et contribuez à la croissance de l'économie.":"Join our network and contribute to economic growth."}</p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <Link to="/register/lender" className={CTA_STYLES.primary} style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}>{ctaTexts.primary}</Link>
            <Link to="/contact" aria-label={ctaTexts.aria} className={`${CTA_STYLES.secondary} border-white/30 text-white`}>{ctaTexts.secondary}</Link>
          </div>
        </div>
      </section>

      <SiteFooter lang={lang} />
      <ScrollToTop />
    </div>
  );
}
